import Idea from "@/types/Idea"
import {EnhancedWorkpace, enhanceWorkspace} from "@/types/Workspace"
import {WorkspacePerson} from "@/types/Person"
import {choiceOption, displayValues, isEmpty, personName, personOf, relatedId, valueText} from "@/utils/propertyDisplay"
import {exactTime} from "@/utils/relativeTime"
import {asBoolean} from "@/utils/utils"
import {t} from "@/i18n"

/**
 * As referências dentro do markdown (pedido do usuário). O texto guarda só a referência, e o nome é
 * sempre o de agora:
 *
 * | Gravado            | O que é                                         |
 * |--------------------|-------------------------------------------------|
 * | `[[cidade#5]]`     | o item 5 da coleção `cidade`                    |
 * | `[[cidade]]`       | a coleção                                       |
 * | `[[@rodmguerra]]`  | a pessoa, pelo alias da conta                   |
 * | `{{prazo}}`        | o valor do campo do próprio item; `{{_createdAt}}` e os outros metacampos, pelos nomes das fórmulas |
 *
 * **Um dono para a ida e a volta.** Para mostrar e para editar, o texto é EXPANDIDO (`expandMentions`):
 * cada referência vira um link comum, que o Toast UI sabe editar e desenhar, e o visualizador reconhece
 * pelo endereço (`mentionLinkOf`). Ao salvar, ele é RECOLHIDO (`collapseMentions`): os links voltam a
 * referência. O nome do item vem do servidor, ao lado do texto (`_mentions`, ver o
 * `markdownMentions.ts` de lá); o da coleção e o da pessoa, do ambiente que a tela já tem.
 *
 * O `{{campo}}` é troca simples pelo alias, sem o motor de fórmulas (decisão do usuário): uma conta ou
 * um caminho se faz numa calculada, e se cita ela.
 */

/**
 * O que o servidor diz de cada `[[coleção#id]]` — o par do `Mention` de lá: o nome de agora, ou que quem
 * lê não a pode abrir, sem dizer se é de quem ele não vê ou se não existe mais (decisão do usuário).
 */
export type Mention = { identifier: string } | { unknown: true }

const ITEM = /\[\[([A-Za-z0-9_-]+)#(\d+)\]\]/g
const COLLECTION = /\[\[([A-Za-z0-9_-]+)\]\]/g
const PERSON = /\[\[@([a-z0-9._-]+)\]\]/g
/**
 * O `{{campo}}` aceita o `_` escapado (`{{\_state}}`): o modo visual do editor escapa o `_` ao gerar o
 * markdown, e o metacampo inserido ali era gravado assim — e não era reconhecido em outro lugar (relato do
 * usuário). A volta (`collapseMentions`) o grava sem a barra; o já gravado com ela também vale.
 */
const FIELD = /\{\{((?:\\_|[A-Za-z0-9_-])+)\}\}/g
const unescaped = (text: string) => text.replace(/\\_/g, '_')

/**
 * Onde estão as referências cruas de um texto — `[[…]]` e `{{…}}`, pelas mesmas expressões da troca —,
 * como pares início e fim. Para o modo Markdown do editor marcá-las (ver `mentionDecorations`).
 */
export function referenceRanges(text: string): Array<[number, number]> {
    return rangesOf(text, [ITEM, COLLECTION, PERSON, FIELD])
}

/** Só os `{{campo}}` — o modo visual do editor desenha o valor no lugar deles (ver `mentionDecorations`). */
export function fieldRanges(text: string): Array<[number, number]> {
    return rangesOf(text, [FIELD])
}

function rangesOf(text: string, patterns: RegExp[]): Array<[number, number]> {
    return patterns.flatMap(pattern =>
        [...text.matchAll(pattern)].map(match => [match.index!, match.index! + match[0].length] as [number, number]))
}

/** O endereço que a pessoa leva no link: não é rota — ninguém navega para uma pessoa (decisão do usuário). */
const PERSON_HREF = '@'

/**
 * Os valores do `{{campo}}` que a tela desenha com cor, também como link sem rota: é o que os leva ao
 * desenho do app pelo mesmo caminho das referências — o visualizador, o resumo, a prévia e o modo visual
 * do editor (pedido do usuário). Como texto, saíam crus.
 *
 * - `boolean`: ✓ Sim em verde, ✗ Não em vermelho, como na grade (`?boolean=true`);
 * - `dot`: a situação, com a bolinha de semáforo na cor dela (`?dot=red`);
 * - `chip`: a opção de domínio, com o fundo tingido (`?chip=blue`).
 */
const VALUE_HREF = /^\?(boolean|dot|chip)=([a-z0-9-]*)$/
const valueHref = (kind: 'boolean' | 'dot' | 'chip', detail: string) => `?${kind}=${detail}`

const itemHref = (system: string, idea: string, id: number | string) => `/workspaces/${system}/collections/${idea}/items/${id}`
const collectionHref = (system: string, idea: string) => `/workspaces/${system}/collections/${idea}`

/** O texto de um link, com os caracteres que o markdown leria como marcação escapados. */
function label(text: string): string {
    return text.replace(/([\\[\]*_`])/g, '\\$1')
}

/** O que a referência precisa da tela: o ambiente, e — para os nomes e os campos — o item e a coleção dele. */
export type MentionContext = {
    workspace: EnhancedWorkpace
    /** O item dono do texto: os valores do `{{campo}}` e o `_mentions` que o servidor mandou. */
    item?: Record<string, any>
    idea?: Idea
    /**
     * Troca o `{{campo}}` pelo valor — no visualizador. No editor fica como está: ele é o modelo que se
     * escreve, e trocado sumiria ao salvar.
     */
    fields?: boolean
}

/**
 * O que o servidor resolveu sem nome não se abre — "Item desconhecido". Qualquer forma sem nome, e não só a
 * `unknown`: o servidor de antes mandava oculto e excluído separados.
 */
const unknownMention = (mention: Mention | undefined) => !!mention && !('identifier' in mention)

/** O nome de um item citado: o de agora, o desconhecido, ou — sem resposta do servidor — o de reserva. */
function itemLabel(mention: Mention | undefined, ideaName: string, id: string): string {
    if (mention && 'identifier' in mention) return mention.identifier
    if (unknownMention(mention)) return t('mention.unknown') as string
    return `${ideaName} #${id}`
}

/**
 * O item que não se abre vai marcado no fragmento do endereço (`…/items/6#unknown`): o visualizador o
 * desenha apagado e sem levar a lugar nenhum, e a volta (`collapseMentions`) o ignora. Sem a marca, ele
 * seria um link para uma página que não abre.
 */
function unavailableOf(mention: Mention | undefined): string {
    return unknownMention(mention) ? '#unknown' : ''
}

/**
 * Troca cada `{{campo}}` pelo valor dele — o campo que não há fica como está. O texto passa por
 * `writeText`; o item de um relacionamento e a pessoa viram referência (`writeLink`, como o `[[…]]`),
 * com a régua e o nome, ou com a foto (pedido do usuário). Um dono para o markdown que se mostra
 * (`expandMentions`) e para o HTML da prévia do editor (`mentionsInHtml`).
 */
export function replaceFields(text: string, context: MentionContext, writeText: (value: string) => string,
                              writeLink: (href: string, label: string) => string): string {
    return text.replace(FIELD, (whole, alias: string) => {
        const parts = fieldParts(unescaped(alias), context)
        if (parts === undefined) return whole
        //Entre duas referências, só o espaço: a régua, a foto ou o sinal já separam uma da outra, como na
        //grade (pedido do usuário) — e o respiro é o do CSS. Entre textos, a vírgula
        return parts.map((part, index) => {
            const separator = !index ? '' : typeof part !== 'string' && typeof parts[index - 1] !== 'string' ? ' ' : ', '
            return separator + (typeof part === 'string' ? writeText(part) : writeLink(part.href, part.label))
        }).join('')
    })
}

/** Um pedaço do valor de um campo: texto, ou uma referência — o item, a pessoa, o sim/não. */
type FieldPart = string | { href: string, label: string }

/** A pessoa como referência (`[[@alias]]`), ou o nome dela, quando não tem alias. */
function personPart(user: unknown, {workspace}: MentionContext): FieldPart[] {
    const person = personOf(user, workspace.people)
    if (!person) return []
    return [person.alias ? {href: `${PERSON_HREF}${person.alias}`, label: personName(person)} : personName(person)]
}

/**
 * O valor de um campo do item, ou de um metacampo, como o texto o mostra. Campo que não há: `undefined`.
 * Sem coleção também — a descrição do ambiente não é de item, e o `{{x}}` dela fica como se escreveu.
 */
function fieldParts(alias: string, context: MentionContext): FieldPart[] | undefined {
    const {workspace, item, idea} = context
    if (!item || !idea) return undefined
    const lookup = {domains: workspace.domains, people: workspace.people}
    if (alias === '_id') return item._instance !== undefined ? [String(item._instance)] : []
    if (alias === '_state') {
        const workflow = workspace.workflowOf(idea)
        const state = workflow && workflow.coloredStateOf(item)
        return state ? [{href: valueHref('dot', state.color || ''), label: state.name}] : []
    }
    if (['_createdAt', '_updatedAt', '_stateChangedAt'].includes(alias)) return item[alias] ? [exactTime(item[alias])] : []
    if (['_createdBy', '_updatedBy'].includes(alias)) return personPart(item[alias], context)
    const property = idea.properties.find(candidate => candidate.alias === alias)
    if (!property) return undefined
    const values = displayValues(item, property).filter(value => !isEmpty(value))
    if (property.type === 'user') return values.flatMap(value => personPart(value, context))
    if (property.type === 'boolean')
        return values.map(value => ({href: valueHref('boolean', String(asBoolean(value))), label: valueText(property, value, lookup)}))
    if (property.type === 'choice') return values.flatMap(value => {
        const option = choiceOption(property, value, workspace.domains)
        return option ? [{href: valueHref('chip', option.color || ''), label: option.name}] : []
    })
    const relationship = property.relationship
    if (relationship) {
        const target = workspace.ideaWith(relationship.idea)
        return values.map(value => {
            const id = relatedId(value)
            return id === undefined ? valueText(property, value, lookup) : {
                href: itemHref(workspace.alias, relationship.idea, id),
                label: (value && value._identifier) || itemLabel(undefined, target ? target.name : relationship.idea, String(id))
            }
        })
    }
    return values.map(value => valueText(property, value, lookup)).filter(Boolean)
}

/**
 * Troca cada referência do texto — item, coleção, pessoa — pelo que `write` faz com o endereço e o nome
 * dela. Um dono para as duas formas que a referência ganha: o link em markdown (`expandMentions`) e o
 * link já em HTML, na prévia do modo Markdown do editor (`mentionsInHtml`, ver `markdownMentionView`).
 * A coleção que não existe mais fica como está.
 */
export function replaceMentions(text: string, {workspace, item}: MentionContext,
                                write: (href: string, label: string) => string): string {
    const system = workspace.alias
    const mentions: Record<string, Mention> = (item && item._mentions) || {}
    return text
        .replace(ITEM, (whole, ideaAlias: string, id: string) => {
            const idea = workspace.ideaWith(ideaAlias)
            const mention = mentions[`${ideaAlias}#${id}`]
            return write(`${itemHref(system, ideaAlias, id)}${unavailableOf(mention)}`, itemLabel(mention, idea ? idea.name : ideaAlias, id))
        })
        .replace(COLLECTION, (whole, ideaAlias: string) => {
            const idea = workspace.ideaWith(ideaAlias)
            return idea ? write(collectionHref(system, ideaAlias), idea.pluralName || idea.name) : whole
        })
        .replace(PERSON, (whole, alias: string) => {
            const person = workspace.people.find(candidate => candidate.alias === alias)
            return write(`${PERSON_HREF}${alias}`, person ? personName(person) : `@${alias}`)
        })
}

/** As referências trocadas por links comuns — para o visualizador desenhar e para o editor editar. */
export function expandMentions(markdown: unknown, context: MentionContext): string {
    if (typeof markdown !== 'string' || !markdown) return ''
    const link = (href: string, text: string) => `[${label(text)}](${href})`
    const expanded = replaceMentions(markdown, context, link)
    return context.fields ? replaceFields(expanded, context, label, link) : expanded
}

/**
 * O markdown de um item como se MOSTRA — no card, na célula, na ficha, no campo somente leitura: as
 * referências em link e o `{{campo}}` no valor. Fora de uma tela de ambiente (sem `workspace`), como
 * está. Um dono para os quatro lugares que desenham o markdown de um item.
 */
export function shownMarkdown(markdown: unknown, workspace: EnhancedWorkpace | null, item: Record<string, any> | undefined,
                              ideaAlias: string | undefined): string {
    if (!workspace) return typeof markdown === 'string' ? markdown : ''
    return expandMentions(markdown, {workspace, item, idea: ideaAlias ? workspace.ideaWith(ideaAlias) : undefined, fields: true})
}

/** A descrição do ambiente e o que o servidor manda ao lado dela (ver o `workspaceDescription` de lá). */
export type Described = {
    description?: string
    /** O nome de agora dos itens que ela cita — o mesmo `_mentions` de um item. */
    _mentions?: Record<string, Mention>
    /**
     * Só na lista da tela inicial, que não tem o catálogo nem as pessoas de cada ambiente: as coleções e
     * as pessoas que a descrição cita (ver `mentionScopeOf`).
     */
    _mentionScope?: { ideas: Idea[], people: WorkspacePerson[] }
}

/**
 * A descrição do ambiente como se mostra — na página dele, no design e na tela inicial: as referências
 * como no markdown de um item, com o `_mentions` que veio com o ambiente. Sem `{{campo}}`: não é de item.
 */
export function shownDescription(workspace: EnhancedWorkpace | null, owner: Described): string {
    return shownMarkdown(owner.description, workspace, owner, undefined)
}

/**
 * O ambiente que a tela inicial tem para desenhar as referências da descrição: só as coleções e as
 * pessoas que ela cita (`_mentionScope`), no mesmo tipo da tela do ambiente — e por isso pelo mesmo
 * `shownDescription` e o mesmo resumo. Sem referência, nenhum.
 */
export function mentionScopeOf(system: Described & { alias: string, title: string }): EnhancedWorkpace | null {
    if (!system._mentionScope) return null
    const {ideas, people} = system._mentionScope
    return enhanceWorkspace({alias: system.alias, title: system.title, ideas, people, workflows: [], domains: []})
}

/**
 * O caminho de volta: os links do próprio ambiente voltam a referência. O resto do texto — os links de
 * fora, o que se escreveu — fica intocado. Aceita os colchetes e o `_` que o editor escapa
 * (`\[\[cidade\_x\]\]`, `{{\_state}}`) quando a referência ou o campo entram no modo visual.
 */
export function collapseMentions(markdown: string, system: string): string {
    const base = `/workspaces/${system.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/collections/`
    const text = '\\[(?:[^\\]\\\\]|\\\\.)*\\]'
    return markdown
        .replace(new RegExp(`${text}\\(${base}([A-Za-z0-9_-]+)/items/(\\d+)(?:#\\w+)?\\)`, 'g'), '[[$1#$2]]')
        .replace(new RegExp(`${text}\\(${base}([A-Za-z0-9_-]+)\\)`, 'g'), '[[$1]]')
        .replace(new RegExp(`${text}\\(${PERSON_HREF}([a-z0-9._-]+)\\)`, 'g'), '[[@$1]]')
        .replace(/\\\[\\\[((?:\\_|[^\]\\])+)\\\]\\\]/g, (whole, inner: string) => `[[${unescaped(inner)}]]`)
        .replace(FIELD, (whole, alias: string) => `{{${unescaped(alias)}}}`)
}

/** O endereço do link de uma referência — o que o editor insere ao escolher no popup. */
export function mentionHref(target: { kind: 'item', idea: string, instance: number } | { kind: 'collection', idea: string }
    | { kind: 'person', alias: string }, system: string): string {
    if (target.kind === 'item') return itemHref(system, target.idea, target.instance)
    if (target.kind === 'collection') return collectionHref(system, target.idea)
    return `${PERSON_HREF}${target.alias}`
}

/** O que um link do texto expandido aponta — para o visualizador desenhar cada um do seu jeito. */
export type MentionLink =
    /** `unavailable`: o item que quem lê não vê, ou que não existe mais — ver `unavailableOf`. */
    | { kind: 'item', idea: string, instance: number, unavailable: boolean }
    | { kind: 'collection', idea: string }
    | { kind: 'person', alias: string }
    | { kind: 'boolean', value: boolean }
    | { kind: 'dot', color: string }
    | { kind: 'chip', color: string }

export function mentionLinkOf(href: string, system: string): MentionLink | undefined {
    if (href.startsWith(PERSON_HREF)) return {kind: 'person', alias: href.slice(PERSON_HREF.length)}
    const value = VALUE_HREF.exec(href)
    if (value) return value[1] === 'boolean' ? {kind: 'boolean', value: value[2] === 'true'}
        : value[1] === 'dot' ? {kind: 'dot', color: value[2]} : {kind: 'chip', color: value[2]}
    const prefix = `/workspaces/${system}/collections/`
    if (!href.startsWith(prefix)) return undefined
    const [path, fragment] = href.slice(prefix.length).split('#')
    const [idea, items, id] = path.split('/')
    if (items === 'items' && id) return {kind: 'item', idea, instance: Number(id), unavailable: !!fragment}
    return items === undefined && idea ? {kind: 'collection', idea} : undefined
}
